import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react';
import { getCosignInbox } from '@/services/clinical';

/**
 * CosignInboxBanner — shared pending-cosign surface for any landing
 * page. Polls /clinical/soap-notes/cosign-inbox every 60s, renders a
 * row per pending note, and routes the user into the editor with the
 * note pre-loaded when they click.
 *
 * Use case: providers who are also owners / office managers do not
 * see the legacy /clinical CosignInbox banner because their dashboard
 * routes them away from /clinical. Dropping this component above the
 * dashboard tabs gives them the same surface.
 *
 * COLLAPSED BY DEFAULT to a one-line pill (Chris Davis 2026-08-03: the
 * full banner shrank the Schedule enough that the appointment panel's
 * chart button fell off-screen). Click expands to the note list; the
 * choice persists per browser in localStorage.
 *
 * Self-hides when the inbox is empty.
 */
const LS_KEY = 'cosign_banner_expanded';

export default function CosignInboxBanner() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
  });
  const { data } = useQuery({
    queryKey: ['cosign-inbox'],
    queryFn: getCosignInbox,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const notes = data?.notes || [];
  if (notes.length === 0) return null;

  function toggle() {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }

  if (!expanded) {
    return (
      <button
        onClick={toggle}
        className="mb-3 flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-left transition-colors hover:bg-amber-100"
        aria-expanded={false}
      >
        <Inbox className="h-3.5 w-3.5 flex-shrink-0 text-amber-700" />
        <span className="text-[12px] font-semibold text-amber-900">
          {notes.length} note{notes.length === 1 ? '' : 's'} awaiting your co-sign
        </span>
        <span className="ml-auto flex items-center gap-1 text-[11px] font-medium text-amber-700">
          Review <ChevronDown className="h-3.5 w-3.5" />
        </span>
      </button>
    );
  }

  return (
    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <button onClick={toggle} className="mb-2 flex w-full items-center gap-2 text-left" aria-expanded>
        <Inbox className="h-4 w-4 text-amber-700" />
        <h2 className="text-[13px] font-bold uppercase tracking-wider text-amber-900">
          {notes.length} note{notes.length === 1 ? '' : 's'} awaiting your co-sign
        </h2>
        <span className="ml-auto flex items-center gap-1 text-[11px] font-medium text-amber-700">
          Minimize <ChevronDown className="h-3.5 w-3.5 rotate-180" />
        </span>
      </button>
      <p className="mb-3 text-[11px] text-amber-700">
        Another provider drafted these and is asking you to review and sign. The claim will bill under your NPI once you sign.
      </p>
      <div className="space-y-1.5">
        {notes.map((n) => (
          <button
            key={n.id}
            onClick={() => navigate(`/clinical?mode=edit&note=${n.id}`)}
            className="flex w-full items-center justify-between rounded-lg border border-amber-300/60 bg-white/80 px-3 py-2 text-left text-[12px] transition-colors hover:bg-white"
          >
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">
                {n.patient_last_name}, {n.patient_first_name}
                {n.chart_number && <span className="ml-1.5 font-mono text-[10px] text-gray-400">#{n.chart_number}</span>}
              </p>
              <p className="text-[11px] text-gray-600">
                {String(n.date_of_service || '').slice(0, 10)} · {n.visit_type || 'visit'}
                {n.drafter_first_name && <> · drafted by {n.drafter_first_name} {n.drafter_last_name}</>}
              </p>
              {n.review_comments && (
                <p className="mt-1 text-[11px] italic text-amber-800">"{n.review_comments}"</p>
              )}
            </div>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-amber-500" />
          </button>
        ))}
      </div>
    </div>
  );
}
